import { useState } from 'react'

import {
  Box,
  CollapsableCard,
  Icon,
  Text,
  Touchable,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Cross } from '@factorialco/f0-react/icons/app'
import { DiscardDuplicateAlertDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'
import { DuplicateExpenseAlert } from 'modules/expenses/hooks/useExpenseAlerts'
import { Expensable } from 'modules/expenses/types/Expensable'

import { DuplicateExpensesCardContent } from './_Content'
import { DuplicateExpensesModal } from './_Modal'

interface Props {
  expensable: Expensable
  duplicateExpenseAlerts: DuplicateExpenseAlert[]
  type: 'duplicate_alerts' | 'category_conflict'
  canOverrideApprovalFlow?: boolean
}

const DuplicateExpensesCard = ({
  expensable,
  duplicateExpenseAlerts,
  type = 'duplicate_alerts',
  canOverrideApprovalFlow,
}: Props) => {
  const [showContent, toggleContent] = useToggle(true)
  const [selectedAlert, setSelectedAlert] = useState<DuplicateExpenseAlert | null>(null)
  const [showDiscardModal, toggleDiscardModal] = useState(false)
  const [showComparisonModal, toggleComparisonModal] = useState(false)

  const {
    access: { locale },
    company: { id: companyId },
  } = useCurrentSession()

  const { onReject, loading: isRejecting } = useExpensableActions()

  const [discardDuplicateAlerts, { loading: isDiscarding }] = useMutation(
    DiscardDuplicateAlertDocument,
    {
      extractorFn: (data) => data.financeMutations.bulkDiscardFinanceSpendingAlertSpendingAlert,
      update: (cache, { data }) => {
        const discardedAlerts =
          data?.financeMutations.bulkDiscardFinanceSpendingAlertSpendingAlert.spendingAlerts || []
        discardedAlerts.forEach((alert) => {
          if (alert) {
            cache.evict({ id: cache.identify(alert) })
          }
        })
        cache.gc()
      },
    }
  )

  const discardAlerts = async (alertIds: number[]) => {
    const result = await discardDuplicateAlerts({
      variables: {
        alertIds,
        companyId,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      if (selectedAlert) {
        const isTarget = expensable.id === selectedAlert.target.id
        const duplicateExpense = isTarget ? selectedAlert.source : selectedAlert.target

        track(trackingEvents.expenses.markNotDuplicate, {
          expense_id: expensable.id,
          duplicate_expense_id: duplicateExpense.id,
          duplicate_expense_status: duplicateExpense.status,
          is_alert_target: isTarget,
        })
      } else {
        track(trackingEvents.expenses.removeAllDuplicateAlerts, {
          expense_id: expensable.id,
          duplicates_count: duplicateExpenseAlerts.length,
        })
      }

      if (showComparisonModal || showDiscardModal) {
        closeModals()
      }
      app.setFlash({
        type: 'correct',
        message:
          type === 'duplicate_alerts'
            ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_success')
            : i18n.t('expenses_v2.expense.category_conflict_alerts.discard_success'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message:
          type === 'duplicate_alerts'
            ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_error')
            : i18n.t('expenses_v2.expense.category_conflict_alerts.discard_error'),
        time: 2000,
      })
    }
  }

  const handleRejectExpense = async (expensableId: number, reason: string, isTarget: boolean) => {
    const handled = await onReject([expensableId], reason, canOverrideApprovalFlow)

    if (handled.success) {
      track(trackingEvents.expenses.rejectDuplicateExpense, {
        expense_id: expensable.id,
        duplicate_expense_id: expensableId,
        is_alert_target: isTarget,
      })
      setSelectedAlert(null)
    }
  }

  const removeAlerts = () => {
    if (selectedAlert) {
      discardAlerts([selectedAlert.id])
    } else {
      discardAlerts(duplicateExpenseAlerts.map((alert) => alert.id))
    }
  }

  const closeModals = () => {
    toggleComparisonModal(false)
    toggleDiscardModal(false)
    setSelectedAlert(null)
  }

  const discardConfirmationText = (alert: DuplicateExpenseAlert | null) => {
    if (alert) {
      return type === 'duplicate_alerts'
        ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_confirmation.description')
        : i18n.t('expenses_v2.expense.category_conflict_alerts.discard_confirmation.description')
    }
    return type === 'duplicate_alerts'
      ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_confirmation.description_all')
      : i18n.t('expenses_v2.expense.category_conflict_alerts.discard_confirmation.description_all')
  }

  const discardConfirmationTitle = (alert: DuplicateExpenseAlert | null) => {
    if (alert) {
      return i18n.t('expenses_v2.expense.duplicate_alerts.discard_confirmation.title')
    }
    return type === 'duplicate_alerts'
      ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_confirmation.title_all')
      : i18n.t('expenses_v2.expense.category_conflict_alerts.discard_confirmation.title_all')
  }

  return (
    <>
      <CollapsableCard
        open={showContent}
        contentBackground="grey000White"
        contentPadding="s12"
        Card={
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
            <Text size="300" weight="semibold" color="radicalRed1200">
              {type === 'duplicate_alerts'
                ? i18n.t('expenses_v2.expense.duplicate_alerts.description', {
                    count: duplicateExpenseAlerts.length,
                  })
                : i18n.t('expenses_v2.expense.category_conflict_alerts.description', {
                    count: duplicateExpenseAlerts.length,
                  })}
            </Text>
            <Touchable onClick={toggleContent(!showContent)}>
              <Icon.Small icon={showContent ? 'chevron-up' : 'chevron-down'} color="grey800" />
            </Touchable>
          </Box>
        }
        Content={
          <Box gap="s24" width="full" paddingX="s4">
            <Box gap="s24">
              {duplicateExpenseAlerts.map((alert) => (
                <DuplicateExpensesCardContent
                  isDiscarding={isDiscarding}
                  key={alert.id}
                  type={type}
                  expensable={alert.target.id === expensable.id ? alert.source : alert.target}
                  locale={locale}
                  onRemove={() => {
                    setSelectedAlert(alert)
                    toggleDiscardModal(true)
                  }}
                  onCompare={() => {
                    setSelectedAlert(alert)
                    toggleComparisonModal(true)
                  }}
                />
              ))}
            </Box>
            <Box width="fit-content">
              <F0Button
                loading={isDiscarding}
                icon={Cross}
                size="md"
                variant="outline"
                label={
                  type === 'duplicate_alerts'
                    ? i18n.t('expenses_v2.expense.duplicate_alerts.remove_all')
                    : i18n.t('expenses_v2.expense.category_conflict_alerts.remove_all')
                }
                onClick={() => {
                  setSelectedAlert(null)
                  toggleDiscardModal(true)
                }}
              />
            </Box>
          </Box>
        }
      />
      {selectedAlert && showComparisonModal && (
        <DuplicateExpensesModal
          locale={locale}
          alert={selectedAlert}
          expensable={expensable}
          type={type}
          isDiscarding={isDiscarding}
          isRejecting={isRejecting}
          onClose={closeModals}
          onRemoveAlert={() => toggleDiscardModal(true)}
          onRejectExpense={handleRejectExpense}
          canOverrideApprovalFlow={canOverrideApprovalFlow}
        />
      )}
      {showDiscardModal && (
        <Modal
          illustration="alert"
          style={{ position: 'relative', top: '200px' }}
          title={discardConfirmationTitle(selectedAlert)}
          footer={
            <Box flexDirection="row" gap="s12">
              <F0Button variant="outline" label={i18n.t('common.cancel')} onClick={closeModals} />
              <F0Button
                label={
                  selectedAlert
                    ? i18n.t('expenses_v2.expense.duplicate_alerts.discard_confirmation.confirm')
                    : i18n.t(
                        'expenses_v2.expense.duplicate_alerts.discard_confirmation.confirm_all'
                      )
                }
                onClick={removeAlerts}
                disabled={isDiscarding}
              />
            </Box>
          }
          onClose={closeModals}
        >
          <Text size="300" color="grey800">
            {discardConfirmationText(selectedAlert)}
          </Text>
        </Modal>
      )}
    </>
  )
}

export { DuplicateExpensesCard }
